'use client';

import { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faClose,
  faShareAlt,
  faSearch,
} from '@fortawesome/pro-solid-svg-icons';
import { useGetListsQuery, useUpdateListMutation } from 'src/api/redux/lists/listsApi';
import { useGetAllCompanyUsersQuery } from 'src/api/redux';
import { useLogging } from 'src/context/logging';
import { useAuth } from 'src/context/auth';
import { useDebounce } from 'use-debounce';
import { Checkbox, CircularProgress, Button } from 'src/components';

interface ShareListDialogProps {
  open: boolean;
  onClose: (event: any, reason?: string) => void;
  listId: number;
}

const ShareListDialog: React.FC<ShareListDialogProps> = ({
  open,
  onClose,
  listId,
}) => {
  const log = useLogging();
  const { user } = useAuth();
  const companyId = user?.company?.id?.toString();

  const { data: providerLists } = useGetListsQuery({});
  const [updateList, { isLoading }] = useUpdateListMutation();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const { data: companyUsers, isLoading: isLoadingCompanyUsers } =
    useGetAllCompanyUsersQuery(
      {
        companyId,
        q: debouncedSearch,
        include_support_user: 1,
      },
      { skip: !companyId }
    );

  const list = providerLists?.results?.find(({ id }) => id === listId);

  const [selectAll, setSelectAll] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<Array<any>>(
    []
  );

  const team: { id: string; label: string }[] = useMemo(() => {
    if (!companyUsers) return [];

    return companyUsers
      ?.filter((u) => u.id !== user?.id)
      ?.map(({ id, first_name, last_name }) => ({
        id: String(id),
        label:
          first_name && last_name
            ? `${first_name} ${last_name}`
            : 'Support User',
      }));
  }, [companyUsers, user]);

  useEffect(() => {
    if (!list) return;
    // Filter out support users from initial selection
    const initialUsers = list.shared_users || [];
    const filteredUsers = initialUsers.filter(
      (user) => user.first_name && user.last_name // Only include users with actual names
    );
    setSelectedTeamMembers(filteredUsers);
    return () => setSelectedTeamMembers([]);
  }, [list]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const selectedTeamMemberIds = selectedTeamMembers.map((u) => u.id);

    try {
      await updateList({ listId: listId.toString(), sharedWith: selectedTeamMemberIds }).unwrap();
      log.event('List shared', { id: listId, name: list?.name });
      setSelectedTeamMembers([]);
      setSearch('');
      setSelectAll(false);
      onClose(e, 'backdropClick');
    } catch (err: any) {
      log.exception('Error sharing list');
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    onClose(e, 'backdropClick');
    setSelectedTeamMembers([]);
    setSearch('');
    setSelectAll(false);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll(e.target.checked);
    if (e.target.checked) {
      setSelectedTeamMembers(team);
    } else {
      setSelectedTeamMembers([]);
    }
  };

  const handleUserSelect = (user: any, checked: boolean) => {
    if (checked) {
      setSelectedTeamMembers((prev) => [user, ...prev]);
    } else {
      setSelectedTeamMembers((prev) => prev.filter((v) => v.id !== user.id));
    }
    if (selectAll) {
      setSelectAll(false);
    }
  };

  const isDirty = useMemo(() => {
    return selectedTeamMembers.length > 0;
  }, [selectedTeamMembers]);

  if (!list) return null;

  return (
    open && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        <div className="fixed inset-0 bg-black/50" onClick={handleCancel} />
        <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="flex items-center text-xl text-gray-600">
              <Icon icon={faShareAlt} className="mr-2 text-gray-600" />
              {`Share ${list?.name || 'List'}`}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-600 hover:text-gray-800"
            >
              <Icon icon={faClose} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4">
            <div className="space-y-4">
              <div className="flex items-center w-full">
                <div className="flex items-center w-full px-3 gap-2 border rounded-md h-10 overflow-hidden">
                  <Icon icon={faSearch} className="w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search Team Members"
                    className="w-full h-full outline-none"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <Icon icon={faClose} className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="h-48 overflow-y-auto border rounded-md p-2">
                {isLoadingCompanyUsers ? (
                  <div className="flex items-center justify-center h-full">
                    <CircularProgress size="sm" color="neutral" />
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {team?.map((user, index) => (
                      <div
                        key={index}
                        className="flex items-center w-4/12 gap-1 text-sm"
                      >
                        <Checkbox
                          checked={selectedTeamMembers.some(
                            (v) => v.id === user.id
                          )}
                          onChange={(e) =>
                            handleUserSelect(user, e.target.checked)
                          }
                        />
                        <span className="truncate">{user.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pl-2">
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                  label="Select All"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outlined"
                color="neutral"
                onClick={handleCancel}
                className="w-full rounded-full"
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="primary"
                disabled={!isDirty || isLoading}
                className="w-full rounded-full"
                loading={isLoading}
              >
                Share
              </Button>
            </div>
          </form>
        </div>
      </div>
    )
  );
};

export default ShareListDialog;
